/**
 * WoHistoryDrawer — the work-order audit trail (migration 110).
 *
 * One chronological feed merging forward edits (WorkOrderEvent), signoff
 * resets (AuditResetLog) and report renders/issued uploads (ReportArtifact),
 * fetched via /workorders/:id/events or /workorders/details/:id/events.
 * Open for a whole WO (toolbar History button) or for one test row (row
 * history icon). `useWoHistory` owns the open/target state so the page adds
 * only a couple of lines.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Chip, CircularProgress, Drawer, IconButton, List, ListItem,
  ListItemIcon, Tooltip, Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import UndoIcon from '@mui/icons-material/Undo';
import EditNoteIcon from '@mui/icons-material/EditNote';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { getWorkOrderTimeline, getDetailTimeline, type WoTimelineEvent } from '../../services/workOrderService';

interface HistoryTarget {
  /** When set, show only this test row's history. */
  detailKey?: string;
  /** Heading context, e.g. "UCS-DEMO-S1 — Rock UCS" or the WO id. */
  label?: string;
}

/** Page-side state holder so WorkOrdersTabContent stays tiny. */
export function useWoHistory() {
  const [target, setTarget] = useState<HistoryTarget | null>(null);
  const open = useCallback((t: HistoryTarget = {}) => setTarget(t), []);
  const close = useCallback(() => setTarget(null), []);
  return { target, open, close };
}

type FilterKey = 'all' | 'signoffs' | 'edits' | 'samples' | 'reports';
const FILTER_TYPES: Record<Exclude<FilterKey, 'all'>, Set<string>> = {
  signoffs: new Set(['signoff', 'status', 'reset']),
  edits: new Set(['edit', 'bulk-edit', 'wo-edit']),
  samples: new Set(['sample-added', 'sample-removed']),
  reports: new Set(['report']),
};
const FILTER_LABELS: Record<FilterKey, string> = {
  all: 'All', signoffs: 'Signoffs & status', edits: 'Edits', samples: 'Samples', reports: 'Reports',
};

const formatWhen = (iso: string): string => {
  const d = new Date(iso);
  const date = d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' });
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  return `${date}, ${time}`;
};

interface WoHistoryDrawerProps {
  workOrderKey: string | undefined;
  workOrderLabel: string;
  target: HistoryTarget | null;
  onClose: () => void;
}

export const WoHistoryDrawer = ({ workOrderKey, workOrderLabel, target, onClose }: WoHistoryDrawerProps) => {
  const theme = useTheme();
  const [events, setEvents] = useState<WoTimelineEvent[] | null>(null);
  const [error, setError] = useState(false);
  const [filter, setFilter] = useState<FilterKey>('all');
  const isOpen = target !== null;

  useEffect(() => {
    if (!isOpen || !workOrderKey) return;
    setEvents(null);
    setError(false);
    setFilter('all');
    const load = target?.detailKey
      ? getDetailTimeline(target.detailKey)
      : getWorkOrderTimeline(workOrderKey);
    load.then(setEvents).catch(() => setError(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, workOrderKey, target?.detailKey]);

  const visible = useMemo(() => {
    if (!events) return [];
    if (filter === 'all') return events;
    const types = FILTER_TYPES[filter];
    return events.filter((e) => types.has(e.type));
  }, [events, filter]);

  const iconFor = (type: string) => {
    switch (type) {
      case 'signoff': return { icon: <HowToRegIcon fontSize="small" />, color: theme.palette.success.main };
      case 'status': return { icon: <SwapHorizIcon fontSize="small" />, color: theme.palette.info.main };
      case 'reset': return { icon: <UndoIcon fontSize="small" />, color: theme.palette.warning.main };
      case 'sample-added': return { icon: <AddCircleOutlineIcon fontSize="small" />, color: theme.palette.success.main };
      case 'sample-removed': return { icon: <RemoveCircleOutlineIcon fontSize="small" />, color: theme.palette.error.main };
      case 'report': return { icon: <DescriptionOutlinedIcon fontSize="small" />, color: theme.palette.primary.main };
      case 'wo-edit': return { icon: <AssignmentOutlinedIcon fontSize="small" />, color: theme.palette.text.secondary };
      default: return { icon: <EditNoteIcon fontSize="small" />, color: theme.palette.text.secondary };
    }
  };

  return (
    <Drawer anchor="right" open={isOpen} onClose={onClose}
      // AppShell's AppBar sits at zIndex drawer+1; go one higher so the
      // drawer header isn't hidden underneath it.
      sx={{ zIndex: (t) => t.zIndex.drawer + 2 }}
      PaperProps={{ sx: { width: { xs: '100%', sm: 460 } } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5, borderBottom: `1px solid ${theme.border.color}` }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>History</Typography>
          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
            {target?.label || workOrderLabel}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} aria-label="Close history"><CloseIcon fontSize="small" /></IconButton>
      </Box>

      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', px: 2, py: 1.25 }}>
        {(Object.keys(FILTER_LABELS) as FilterKey[]).map((key) => (
          <Chip key={key} size="small" label={FILTER_LABELS[key]}
            color={filter === key ? 'primary' : 'default'}
            variant={filter === key ? 'filled' : 'outlined'}
            onClick={() => setFilter(key)} />
        ))}
      </Box>

      {error && (
        <Typography variant="body2" color="error" sx={{ px: 2, py: 3 }}>
          Could not load the history. Close and try again.
        </Typography>
      )}
      {!error && events === null && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>
      )}
      {!error && events !== null && visible.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 3 }}>
          {events.length === 0
            ? 'No history recorded yet. Changes made from now on will appear here.'
            : 'No entries match this filter.'}
        </Typography>
      )}

      <List dense sx={{ overflowY: 'auto', px: 1, pb: 2 }}>
        {visible.map((e) => {
          const { icon, color } = iconFor(e.type);
          const who = e.userName || e.userEmail;
          const showContext = !target?.detailKey && !!e.sampleId;
          const valueChip = (text: string, removed: boolean) => (
            <Chip size="small" label={text} variant={removed ? 'outlined' : 'filled'}
              sx={{
                height: 20, fontSize: '0.72rem', maxWidth: 220,
                ...(removed
                  ? { textDecoration: 'line-through', color: theme.palette.text.secondary }
                  : { backgroundColor: alpha(color, 0.12), color: theme.palette.text.primary }),
              }} />
          );
          return (
            <ListItem key={e.key} alignItems="flex-start"
              sx={{ borderRadius: 2, '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.04) } }}>
              <ListItemIcon sx={{ minWidth: 34, mt: 0.25, color }}>{icon}</ListItemIcon>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {/* Headline + (bulk) marker; full technical sentence on hover */}
                <Tooltip title={e.summary} enterDelay={800}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{e.title || e.summary}</Typography>
                    {e.type === 'bulk-edit' && (
                      <Chip size="small" label="bulk" variant="outlined"
                        sx={{ height: 17, fontSize: '0.65rem', color: theme.palette.text.secondary }} />
                    )}
                  </Box>
                </Tooltip>
                {/* Old → new value chips */}
                {e.changes.length > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 0.5, rowGap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                    {e.changes.map((c, i) => (
                      <Box key={`${c.label}-${i}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {e.changes.length > 1 && (
                          <Typography variant="caption" color="text.secondary">{c.label}</Typography>
                        )}
                        {c.from != null && valueChip(c.from, true)}
                        {c.from != null && c.to != null && (
                          <ArrowRightAltIcon sx={{ fontSize: 15, color: theme.palette.text.secondary }} />
                        )}
                        {c.to != null && valueChip(c.to, false)}
                      </Box>
                    ))}
                  </Box>
                )}
                {/* Which sample / test (WO-level view only — row view says it in the header) */}
                {showContext && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, minWidth: 0 }}>
                    <ScienceOutlinedIcon sx={{ fontSize: 13, color: theme.palette.text.secondary }} />
                    <Typography variant="caption" sx={{ fontWeight: 600 }} noWrap>
                      {[e.sampleId, e.specimenRef].filter(Boolean).join(' / ')}
                    </Typography>
                    {e.testName && (
                      <Typography variant="caption" color="text.secondary" noWrap>· {e.testName}</Typography>
                    )}
                  </Box>
                )}
                {/* Who + when */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                  <PersonOutlineIcon sx={{ fontSize: 13, color: theme.palette.text.secondary }} />
                  <Typography variant="caption" color="text.secondary">
                    <Tooltip title={e.userEmail}><Box component="span" sx={{ fontWeight: 600 }}>{who}</Box></Tooltip>
                    {` · ${formatWhen(e.timestamp)}`}
                  </Typography>
                </Box>
              </Box>
            </ListItem>
          );
        })}
      </List>
    </Drawer>
  );
};
